/**
 * Pure financing-calculator math, extracted from the calculator components so it
 * can be unit-tested in isolation (no React, no MUI slider, no CMS fetch).
 *
 * All six financing calculators (Personal, TD, Micro-PFi, CCSP, Umrah, Motorcycle,
 * Professional-PFi) share the exact same installment formula and rate-table shape;
 * only their min/max amount bounds and the CMS field they read from differ. This
 * module is the single source of truth for that shared math.
 */

/**
 * A repayment-rate row as it arrives from Strapi: a tenure (years) and a
 * percentage rate. BOTH fields are optional because Strapi models component
 * fields as nullable — the generated spec types say so, and a half-filled row
 * really does reach here from the CMS.
 */
export type RateRow = {
  tenure?: number | null
  /** Percentage value, e.g. `5.78` means 5.78%. */
  rate?: number | null
}

/** Tenure (years) → rate as a fraction, e.g. `{ 1: 0.0578 }`. */
export type RateTable = Record<number, number>

export type FinancingValidation =
  { status: 'valid'; amount: number } | { status: 'required' } | { status: 'out-of-range' }

/**
 * Round a financing amount to the nearest RM100. The calculators snap the
 * user's entered amount before computing the installment.
 *
 * Uses `Math.round`, so exact halves round up: 10050 → 10100, 10049 → 10000.
 */
export function roundToNearest100(value: number): number {
  return Math.round(value / 100) * 100
}

/**
 * Estimated monthly installment for flat-rate Islamic financing.
 *
 *   total profit   = principal × rate × tenure(years)
 *   total payable  = principal + total profit
 *   monthly        = total payable / (tenure × 12 months)
 *
 * @param amount Principal in RM (already rounded by the caller).
 * @param rate   Profit rate as a fraction (e.g. 0.0578 for 5.78%).
 * @param tenure Tenure in years (must be >= 1).
 */
export function calculateMonthlyInstallment(amount: number, rate: number, tenure: number): number {
  return (amount * rate * tenure + amount) / (tenure * 12)
}

/**
 * Format an installment amount the way the calculators display it: Malaysian
 * locale, always two decimal places (e.g. 1205.8333 → "1,205.83").
 */
export function formatInstallment(value: number): string {
  return value.toLocaleString('ms-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

/**
 * Validate the raw financing-amount string the user typed.
 *
 * Returns a status the component maps to a localized message — keeping this
 * function free of any English/Malay copy:
 *   - `required`     → empty / non-numeric input
 *   - `out-of-range` → outside [min, max]
 *   - `valid`        → in range, with the amount rounded to the nearest RM100
 */
export function validateFinancingAmount(
  rawValue: string,
  bounds: { min: number; max: number }
): FinancingValidation {
  const amount = Number.parseFloat(rawValue)

  if (Number.isNaN(amount) || rawValue === '') {
    return { status: 'required' }
  }

  if (amount < bounds.min || amount > bounds.max) {
    return { status: 'out-of-range' }
  }

  return { status: 'valid', amount: roundToNearest100(amount) }
}

/**
 * Transform the CMS rate array into a tenure→fraction lookup, dividing each
 * percentage rate by 100. Returns `{}` for missing/empty data so callers can
 * safely `Object.keys()` it before the real data has loaded.
 */
export function parseRateTable(rows?: RateRow[] | null | undefined): RateTable {
  if (!rows) return {}
  return rows.reduce<RateTable>((acc, row) => {
    // Skip half-filled rows. Without this an unset tenure/rate writes
    // `acc[undefined] = NaN`, which reaches the customer as a NaN instalment.
    if (typeof row.tenure !== 'number' || typeof row.rate !== 'number') return acc
    acc[row.tenure] = row.rate / 100
    return acc
  }, {})
}

/**
 * Coerce a possibly-non-finite value to a finite number, falling back when it
 * is not. The calculators derive slider min/max/value from CMS data that is
 * empty on first render, yielding `NaN` (e.g. `parseInt(Object.keys({})[0])`);
 * passing `NaN` to an `<input>` triggers React "Received NaN" warnings.
 */
export function toFiniteNumber(input: unknown, fallback: number): number {
  const parsed = Number(input)
  return Number.isFinite(parsed) ? parsed : fallback
}
